
import React, { useState } from 'react';
import { RegistrationFormValues } from '@/schemas/registrationSchema';
import { useToast } from '@/hooks/use-toast';
import { checkDuplicateRegistration } from '@/utils/registrationUtils';

interface FormHandlersProps {
  onSubmitSuccess: (data: RegistrationFormValues) => void;
  setIsSubmitting: (value: boolean) => void;
  setFocus: (field: any) => void;
  setError: (field: any, error: { type: string; message: string }) => void;
}

export const useFormHandlers = ({
  onSubmitSuccess,
  setIsSubmitting,
  setFocus,
  setError
}: FormHandlersProps) => {
  const { toast } = useToast();

  // Function to scroll to an invalid field
  const scrollToFirstError = (errors: Record<string, any>, formRef: React.RefObject<HTMLFormElement>) => {
    if (!formRef.current) return;
    
    const firstErrorKey = Object.keys(errors)[0];
    if (!firstErrorKey) return;
    
    const errorElement = formRef.current.querySelector(`[name="${firstErrorKey}"], #${firstErrorKey}`);
    if (errorElement) {
      // Scroll to the element with a slight offset
      errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Try to focus the element
      if (errorElement instanceof HTMLElement) {
        setTimeout(() => {
          errorElement.focus();
        }, 500);
      }
    }
  };

  const handleFormSubmit = async (data: RegistrationFormValues) => {
    setIsSubmitting(true);
    
    try {
      // Log the form data to help with debugging
      console.log("Form submission data:", data);
      
      // Make sure required fields are present
      const missingFields = [];
      if (!data.fullName?.trim()) missingFields.push("Họ và tên");
      if (!data.email?.trim()) missingFields.push("Email");
      if (!data.phone?.trim()) missingFields.push("Số điện thoại");
      if (!data.class?.trim()) missingFields.push("Lớp");
      if (!data.t_shirt_size?.trim()) missingFields.push("Size áo");
      
      // Show specific errors if fields are missing
      if (missingFields.length > 0) {
        toast({
          title: "Vui lòng kiểm tra lại thông tin",
          description: `Các trường sau chưa được điền: ${missingFields.join(', ')}`,
          variant: "destructive"
        });
        
        // Set focus on the first missing field
        if (missingFields[0] === "Họ và tên") setFocus('fullName');
        else if (missingFields[0] === "Email") setFocus('email');
        else if (missingFields[0] === "Số điện thoại") setFocus('phone');
        else if (missingFields[0] === "Lớp") setFocus('class');
        else if (missingFields[0] === "Size áo") setFocus('t_shirt_size');
        
        setIsSubmitting(false);
        return;
      }
      
      // Simulate network delay for spinner display (500ms)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check for duplicates before submitting
      const { isDuplicate, message, field } = checkDuplicateRegistration(data);
      
      if (isDuplicate && message) {
        // Show error toast for duplicate registration
        toast({
          title: "Đăng ký không thành công",
          description: message,
          variant: "destructive"
        });
        
        // Set focus on the field with the duplicate data
        if (field) {
          setError(field, { 
            type: 'manual', 
            message 
          });
          setFocus(field);
        }
        
        setIsSubmitting(false);
        return;
      }
      
      // Proceed with registration if no duplicates
      const fullData: RegistrationFormValues = {
        ...data,
        additionalContribution: data.additionalContribution || 0
      };
      
      onSubmitSuccess(fullData);
      
      // Remove form data saved in localStorage since registration was successful
      localStorage.removeItem('registration-form');
    } catch (error) {
      console.error("Form submission error:", error);
      setIsSubmitting(false);
      
      toast({
        title: "Có lỗi xảy ra",
        description: "Vui lòng thử lại sau. Chi tiết: " + (error instanceof Error ? error.message : "Lỗi không xác định"),
        variant: "destructive"
      });
    }
  };

  const handleValidationError = (errors: Record<string, any>, formRef: React.RefObject<HTMLFormElement>) => {
    // Log validation errors for debugging
    console.log("Validation errors:", errors);
    
    // This callback runs when validation fails
    scrollToFirstError(errors, formRef);
    
    // Show detailed error toast
    const errorKeys = Object.keys(errors);
    const errorFields = errorKeys.map(key => {
      switch (key) {
        case 'fullName': return 'Họ và tên';
        case 'email': return 'Email';
        case 'phone': return 'Số điện thoại';
        case 'class': return 'Lớp';
        case 't_shirt_size': return 'Size áo';
        default: return key;
      }
    }).join(', ');
    
    toast({
      title: "Vui lòng kiểm tra lại thông tin",
      description: `Các trường sau chưa hợp lệ: ${errorFields}`,
      variant: "destructive"
    });
  };

  return {
    handleFormSubmit,
    handleValidationError
  };
};
